<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BrandingSettingAPI from '../api/brandingSetting';

const isLoading = ref(true);
const isSaving = ref(false);

const brandName = ref('');
const logoUrl = ref('');
const faviconUrl = ref('');

const logoFile = ref(null);
const faviconFile = ref(null);
const logoPreview = ref('');
const faviconPreview = ref('');

const logoInputRef = ref(null);
const faviconInputRef = ref(null);

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await BrandingSettingAPI.get();
    brandName.value = data?.brand_name || '';
    logoUrl.value = data?.logo_url || '';
    faviconUrl.value = data?.favicon_url || '';
  } catch (error) {
    useAlert('Não foi possível carregar a configuração de marca');
  } finally {
    isLoading.value = false;
  }
};

const onFileChange = (event, fileRef, previewRef) => {
  const file = event.target.files?.[0];
  if (!file) return;
  fileRef.value = file;
  previewRef.value = URL.createObjectURL(file);
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await BrandingSettingAPI.update({
      brandName: brandName.value,
      logoFile: logoFile.value,
      faviconFile: faviconFile.value,
    });
    brandName.value = data?.brand_name || brandName.value;
    logoUrl.value = data?.logo_url || logoUrl.value;
    faviconUrl.value = data?.favicon_url || faviconUrl.value;
    logoFile.value = null;
    faviconFile.value = null;
    logoPreview.value = '';
    faviconPreview.value = '';
    if (logoInputRef.value) logoInputRef.value.value = '';
    if (faviconInputRef.value) faviconInputRef.value.value = '';
    useAlert(
      'Marca atualizada com sucesso. Pode levar alguns segundos para refletir em toda a interface.'
    );
  } catch (error) {
    useAlert('Não foi possível salvar a configuração de marca');
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 overflow-y-auto bg-n-background p-6">
    <header class="flex-shrink-0 mb-6">
      <h1 class="text-base font-medium text-n-slate-12 m-0">Marca (Branding)</h1>
      <p class="text-sm text-n-slate-11 m-0 max-w-xl">
        Nome, logo e favicon usados em toda a interface. O nome também
        substitui automaticamente todas as ocorrências da palavra
        "Chatwoot" no frontend, em qualquer idioma.
      </p>
    </header>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <div v-else class="flex flex-col gap-8 max-w-xl">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12" for="brand-name-input">
          Nome da instalação
        </label>
        <input
          id="brand-name-input"
          v-model="brandName"
          type="text"
          placeholder="Ex: Vip Home Care"
          class="reset-base w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
        />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Logo</span>
        <div class="flex items-center gap-4">
          <div
            class="flex items-center justify-center w-20 h-20 rounded-lg bg-n-alpha-1 overflow-hidden flex-shrink-0"
          >
            <img
              v-if="logoPreview || logoUrl"
              :src="logoPreview || logoUrl"
              alt="Logo"
              class="object-contain w-full h-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <input
              ref="logoInputRef"
              type="file"
              accept="image/*"
              class="text-sm text-n-slate-11"
              @change="e => onFileChange(e, logoFile, logoPreview)"
            />
            <span class="text-xs text-n-slate-10">
              Usado no painel e na tela de login. Formato recomendado: SVG ou PNG.
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Favicon</span>
        <div class="flex items-center gap-4">
          <div
            class="flex items-center justify-center w-12 h-12 rounded-lg bg-n-alpha-1 overflow-hidden flex-shrink-0"
          >
            <img
              v-if="faviconPreview || faviconUrl"
              :src="faviconPreview || faviconUrl"
              alt="Favicon"
              class="object-contain w-full h-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <input
              ref="faviconInputRef"
              type="file"
              accept="image/*"
              class="text-sm text-n-slate-11"
              @change="e => onFileChange(e, faviconFile, faviconPreview)"
            />
            <span class="text-xs text-n-slate-10">
              Ícone da aba do navegador. Formato recomendado: PNG 512x512.
            </span>
          </div>
        </div>
      </div>

      <Button
        label="Salvar"
        size="sm"
        class="w-fit"
        :is-loading="isSaving"
        @click="save"
      />
    </div>
  </div>
</template>
